import React, { useState, useEffect } from 'react';
import { tasksApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from './ui/tabs';
import { Save, X } from 'lucide-react';
import { toast } from 'sonner';
import ActivityLog from './ActivityLog';
import Comments from './Comments';
import Attachments from './Attachments';

const columns = [
  { id: 'todo', label: 'To Do' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'review', label: 'In Review' },
  { id: 'done', label: 'Complete' },
];

const TaskEditDialog = ({ task, open, onOpenChange, users = [], projects = [], onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        status: task.status || 'todo',
        assignee_id: task.assignee_id || '',
        start_date: task.start_date || '',
        due_date: task.due_date || '',
        estimated_hours: task.estimated_hours || 0,
        request_type: task.request_type || '',
        project_id: task.project_id || '',
        recurrence: task.recurrence || '',
        is_recurring_template: task.is_recurring_template || false,
      });
      setActiveTab('details');
    }
  }, [task]);

  const handleSave = async () => {
    if (!task?.id) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean up empty strings for optional fields
      if (!payload.assignee_id) delete payload.assignee_id;
      if (!payload.start_date) delete payload.start_date;
      if (!payload.due_date) delete payload.due_date;
      if (!payload.project_id) delete payload.project_id;

      await tasksApi.update(task.id, payload);
      toast.success('Task updated');
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="task-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Amaranth'] text-[#1A2B5E]">Edit Task</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments">Files</TabsTrigger>
            <TabsTrigger value="comments" data-testid="tab-comments">Comments</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                data-testid="edit-task-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3}
                data-testid="edit-task-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {columns.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assignee</Label>
                <Select value={form.assignee_id || 'unassigned'} onValueChange={v => setForm({ ...form, assignee_id: v === 'unassigned' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estimated Hours</Label>
                <Input
                  type="number"
                  value={form.estimated_hours}
                  onChange={e => setForm({ ...form, estimated_hours: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Request Type</Label>
                <Select value={form.request_type || 'none'} onValueChange={v => setForm({ ...form, request_type: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="document">Provide Documents</SelectItem>
                    <SelectItem value="update">Provide Update</SelectItem>
                    <SelectItem value="review">Review Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {projects.length > 0 && (
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select value={form.project_id || 'none'} onValueChange={v => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Standalone</SelectItem>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Recurrence</Label>
              <div className="grid grid-cols-2 gap-4">
                <Select value={form.recurrence || 'none'} onValueChange={v => setForm({ ...form, recurrence: v === 'none' ? '' : v, is_recurring_template: v !== 'none' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No repeat</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-navy/10">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={saving || !form.title?.trim()}
                className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
                data-testid="save-task-btn"
              >
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="attachments" className="mt-4">
            <Attachments entityType="task" entityId={task.id} />
          </TabsContent>

          <TabsContent value="comments" className="mt-4">
            <Comments entityType="task" entityId={task.id} />
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <ActivityLog entityType="task" entityId={task.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default TaskEditDialog;
